<template>
  <div>
    <v-dialog
      v-model="internalValue"
      max-width="1100px"
      transition="dialog-bottom-transition"
      scrollable
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;">mdi-account-group</v-icon>
          <span class="ds-dialog-title">Pacientes do Médico</span>
          <v-spacer />
          <v-btn
            small
            outlined
            color="white"
            class="mr-2"
            style="position: relative; z-index: 1;"
            data-tour="dp-pauta"
            @click="openPauta"
          >
            <v-icon left small>mdi-lightbulb-on-outline</v-icon>
            Pauta Inteligente
          </v-btn>
          <v-btn
            icon
            class="close-btn"
            color="white"
            aria-label="Ver tour desta janela"
            @click="startTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon class="close-btn" color="white" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ds-dialog-body">
          <!-- STEPPER -->
          <v-stepper v-model="currentStep" elevation="0" class="stepper-custom transparent">
            <v-stepper-header class="steps-header">
              <v-stepper-step
                :complete="currentStep > 1"
                step="1"
                color="primary"
                data-tour="dp-lista"
              >
                Lista de Pacientes
                <small>Visualizar relações</small>
              </v-stepper-step>

              <v-divider></v-divider>

              <v-stepper-step
                :complete="currentStep > 2"
                step="2"
                color="success"
                data-tour="dp-add"
              >
                Adicionar Relação
                <small>Vincular novo paciente</small>
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <!-- STEP 1: LISTA DE PACIENTES -->
              <v-stepper-content step="1" class="px-0 pb-0">
                <div>
                  <!-- Botão de adicionar em destaque -->
                  <div class="text-center mb-6" v-if="userType === 'assistant'">
                    <v-btn
                      large
                      color="success"
                      class="elevation-4 add-patient-btn"
                      @click="goToStep2"
                    >
                      <v-icon left size="24">mdi-plus-circle</v-icon>
                      Adicionar Novo Paciente
                    </v-btn>
                  </div>

                  <!-- Lista de pacientes relacionados -->
                  <section class="ds-form-section">
                    <div class="ds-form-section__head">
                      <div class="ds-form-section__title">
                        <v-icon small color="primary" class="mr-2">mdi-account-group</v-icon>
                        <span>Pacientes Vinculados</span>
                      </div>
                      <span v-if="!loadingRelatedPatients" class="ds-form-section__aside">{{ patientsRelated.length }}</span>
                    </div>

                    <v-container fluid class="pa-0">
                      <v-row
                        v-if="loadingRelatedPatients"
                        justify="center"
                        class="my-10"
                      >
                        <v-progress-circular
                          indeterminate
                          color="primary"
                          size="50"
                        ></v-progress-circular>
                      </v-row>

                      <v-row
                        v-if="!loadingRelatedPatients && patientsRelated.length"
                        dense
                      >
                        <v-col
                          v-for="patient in patientsRelated"
                          :key="patient.id"
                          cols="12"
                          sm="6"
                          md="4"
                        >
                          <v-card outlined class="patient-card" hover>
                            <v-card-title class="pa-3">
                              <v-avatar size="50" class="mr-3">
                                <v-img :src="patient.imageUrl" />
                              </v-avatar>
                              <div class="flex-grow-1">
                                <div class="font-weight-medium text-subtitle-1">
                                  {{ patient.name }}
                                </div>
                                <div class="text-caption grey--text">
                                  {{ patient.email }}
                                </div>
                                <div
                                  class="text-caption primary--text"
                                  v-if="patient.age"
                                >
                                  {{ patient.age }} anos
                                </div>
                              </div>
                            </v-card-title>

                            <v-card-actions class="pa-3 pt-0">
                              <v-btn
                                color="error"
                                text
                                small
                                @click="confirmDeletePatient(patient.id)"
                              >
                                <v-icon left small>mdi-delete</v-icon>
                                Remover
                              </v-btn>
                            </v-card-actions>
                          </v-card>
                        </v-col>
                      </v-row>

                      <div
                        v-else
                        class="text-center py-6"
                      >
                        <v-icon size="64" color="grey lighten-1" class="mb-4">
                          mdi-account-group-outline
                        </v-icon>
                        <div class="text-h6 grey--text mb-2">
                          Nenhum paciente vinculado
                        </div>
                        <div class="text-body-2 grey--text mb-4">
                          Adicione pacientes para começar a gerenciar as relações
                        </div>
                        <v-btn
                          color="primary"
                          @click="goToStep2"
                          v-if="userType === 'assistant'"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Adicionar Primeiro Paciente
                        </v-btn>
                      </div>
                    </v-container>
                  </section>
                </div>
              </v-stepper-content>

              <!-- STEP 2: FORMULÁRIO DE ADIÇÃO -->
              <v-stepper-content step="2" class="px-0 pb-0">
                <section class="ds-form-section">
                  <div class="ds-form-section__head">
                    <div class="ds-form-section__title">
                      <v-icon small color="primary" class="mr-2">mdi-link-variant</v-icon>
                      <span>Nova relação</span>
                    </div>
                  </div>

                  <v-alert type="info" text dense class="mb-4">
                    <div class="font-weight-medium">Vincular Novo Paciente</div>
                    <div class="text-caption">
                      Selecione a clínica e o paciente para criar uma nova
                      relação
                    </div>
                  </v-alert>

                  <v-form ref="relationForm" v-model="formValid">
                    <!-- Só mostra selects se for ASSISTENTE -->
                    <template v-if="userType === 'assistant'">
                      <!-- Select da clínica -->
                      <v-row>
                        <v-col cols="12">
                          <v-select
                            v-model="selectedClinic"
                            :items="clinics"
                            item-text="name"
                            item-value="id"
                            label="Selecione a clínica"
                            prepend-inner-icon="mdi-hospital-building"
                            outlined
                            color="primary"
                            clearable
                            :rules="[(v) => !!v || 'Selecione uma clínica']"
                            @change="onClinicChange"
                            class="clinic-select"
                          >
                            <template v-slot:item="{ item }">
                              <div class="py-2">
                                <div class="font-weight-medium">
                                  {{ item.name }}
                                </div>
                                <div class="text--secondary text-caption">
                                  CNPJ: {{ item.cnpj }} - {{ item.city }}/{{
                                    item.state
                                  }}
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>

                      <!-- Select do paciente -->
                      <v-row>
                        <v-col cols="12">
                          <v-select
                            label="Selecione o paciente"
                            v-model="selectedPatient"
                            :items="patients"
                            item-text="name"
                            item-value="id"
                            prepend-inner-icon="mdi-account-group"
                            outlined
                            color="primary"
                            clearable
                            :disabled="!selectedClinic"
                            :loading="loadingPatients"
                            :rules="[(v) => !!v || 'Selecione um paciente']"
                            class="patient-select"
                          >
                            <template v-slot:item="{ item }">
                              <div class="py-2">
                                <div class="font-weight-medium">
                                  {{ item.name }}
                                </div>
                                <div class="text--secondary text-caption">
                                  Email: {{ item.email }}
                                  <span v-if="item.age">
                                    - {{ item.age }} anos</span
                                  >
                                </div>
                              </div>
                            </template>
                          </v-select>
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Ações do formulário -->
                    <v-row class="mt-4">
                      <v-col cols="12">
                        <div class="d-flex justify-space-between">
                          <v-btn outlined @click="goToStep1" color="primary">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar
                          </v-btn>

                          <v-btn
                            color="success"
                            :disabled="!selectedPatient || !formValid"
                            :loading="creatingRelation"
                            @click="createRelation"
                            class="px-6"
                            depressed
                          >
                            <v-icon left>mdi-link</v-icon>
                            Vincular Paciente
                          </v-btn>
                        </div>
                      </v-col>
                    </v-row>
                  </v-form>
                </section>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-card>
    </v-dialog>

    <PautaInteligenteDialog v-model="pautaDialog" :doctor-id="item && item.id" />
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { getMyClinics, getPatientsByClinics } from "@/services/clinicService";
import { getProfileImage } from "@/utils/getProfileImage";
import PautaInteligenteDialog from "./PautaInteligenteDialog.vue";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "DoctorPatientsDialog",
  mixins: [tourDialogMixin],
  components: { PautaInteligenteDialog },
  props: {
    item: { type: Object, required: true },
    value: { type: Boolean, default: false },
  },
  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },
  data() {
    return {
      currentStep: 1,
      patients: [],
      patientsRelated: [],
      selectedPatient: null,
      selectedClinic: null,
      clinics: [],
      patientToDelete: null,
      formValid: false,
      loadingPatients: false,
      creatingRelation: false,
      deletingRelation: false,
      loadingRelatedPatients: false,
      pautaDialog: false,
      _patientBlobUrls: new Set(),
    };
  },
  computed: {
    tourId() {
      return "dialog-pacientes-medico";
    },
    userType() {
      return this.$store.getters["auth/userType"];
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val) {
          this.tryOpen();
          this.tourOnOpen(); // tour guiado na 1ª vez que abre
        } else this.resetDialog();
      },
    },
    "item.id": {
      immediate: true,
      handler() {
        if (this.value) this.tryOpen();
      },
    },
  },
  beforeDestroy() {
    this.revokePatientBlobs();
  },
  methods: {
    tourSteps() {
      return [
        {
          title: "Pacientes do médico",
          description:
            "Aqui você vê e gerencia os pacientes vinculados a este médico — a base de quem ele pode atender na agenda.",
        },
        {
          anchor: "dp-lista",
          title: "Lista de pacientes",
          description:
            "Nesta etapa aparecem os pacientes já vinculados ao médico. Você pode consultar e, se precisar, remover vínculos.",
        },
        {
          anchor: "dp-add",
          title: "Adicionar relação",
          description:
            "Para vincular um novo paciente ao médico, vá para esta etapa: escolha a clínica e o paciente e confirme.",
        },
        {
          anchor: "dp-pauta",
          title: "Pauta Inteligente",
          description:
            "Atalho para a Pauta Inteligente deste médico: sugestões de quem chamar para a agenda com base no histórico e nas pendências dos pacientes.",
        },
      ];
    },

    // ================= LIFECYCLE =================
    async onOpen() {
      await this.init();
      await this.fetchData();
    },
    async tryOpen() {
      if (!this.item?.id) return;

      await this.$nextTick();
      await this.onOpen();
    },
    async init() {
      await this.$store.dispatch("auth/ensureAuth");
    },
    async fetchData() {
      await this.getMyPatientsByMyDoctorId();
    },
    closeDialog() {
      this.$emit("input", false);
    },
    openPauta() {
      if (!this.item?.id) {
        this.warning("Médico não identificado.");
        return;
      }
      this.pautaDialog = true;
    },
    resetDialog() {
      this.currentStep = 1;
      this.patients = [];
      this.selectedPatient = null;
      this.patientsRelated = [];
      this.selectedClinic = null;
      this.clinics = [];
      this.formValid = false;

      this.revokePatientBlobs();

      this.$refs.form?.resetValidation?.();
    },
    // ================= BLOB CLEANUP =================
    revokePatientBlobs() {
      if (!this._patientBlobUrls) {
        this._patientBlobUrls = new Set();
        return;
      }
      for (const url of this._patientBlobUrls) {
        try {
          if (url && String(url).startsWith("blob:")) URL.revokeObjectURL(url);
        } catch {}
      }
      this._patientBlobUrls.clear();
    },
    // ================= STEPS =================
    goToStep1() {
      this.currentStep = 1;
      this.selectedPatient = null;
      this.selectedClinic = null;
    },
    async goToStep2() {
      if (this.userType !== "assistant") return;
      this.currentStep = 2;
      await this.setupRelationForm();
    },
    async setupRelationForm() {
      if (this.userType !== "assistant") return;
      this.clinics = (await getMyClinics()) || [];
      if (this.clinics.length > 0) {
        this.selectedClinic = this.clinics[0].id;
        await this.onClinicChange();
      }
    },
    async onClinicChange() {
      if (!this.selectedClinic) {
        this.patients = [];
        return;
      }
      if (this.userType !== "assistant") return;
      this.loadingPatients = true;
      try {
        this.patients = (await getPatientsByClinics(this.selectedClinic)) || [];
      } catch (err) {
        console.error("Erro ao carregar pacientes:", err);
        this.patients = [];
      } finally {
        this.loadingPatients = false;
      }
    },
    // ================= LISTAGEM =================
    async getMyPatientsByMyDoctorId() {
      if (!this.item?.id) return;
      this.loadingRelatedPatients = true;
      this.revokePatientBlobs();
      try {
        const { data } = await http.get(
          `/api/relation/patients-by-doctor/${this.item.id}`,
        );
        const patientsRaw = data?.data || [];
        this.patientsRelated = await Promise.all(
          patientsRaw.map(async (patient) => {
            // se backend já mandar URL pronta, usa
            if (patient.profile_image_url) {
              return { ...patient, imageUrl: patient.profile_image_url };
            }
            // senão usa util (que já tem fallback + http padronizado)
            const imageUrl = await getProfileImage(patient.profile_image);
            if (typeof imageUrl === "string" && imageUrl.startsWith("blob:")) {
              if (!this._patientBlobUrls) this._patientBlobUrls = new Set();
              this._patientBlobUrls.add(imageUrl);
            }
            return { ...patient, imageUrl };
          }),
        );
      } catch (err) {
        console.error("Erro ao buscar pacientes:", err);
        this.patientsRelated = [];
      } finally {
        this.loadingRelatedPatients = false;
      }
    },
    // ================= RELAÇÃO =================
    async createRelation() {
      if (!this.selectedPatient || !this.item?.id) return;

      this.creatingRelation = true;

      const relation = {
        doctor_id: this.item.id,
        patient_id: this.selectedPatient.id ?? this.selectedPatient,
        relation_type: "doctor_patient",
      };

      try {
        await http.post(`/api/relation/doctor-patient`, relation);

        this.success("Relação criada com sucesso!");
        await this.getMyPatientsByMyDoctorId();
        this.goToStep1();
      } catch (err) {
        if (err.response?.status === 409) {
          this.warning("Esta relação já existe.");
        } else {
          console.error("Erro ao criar relação:", err);
          this.erroDeRequisicao(err, "Erro ao criar relação.");
        }
      } finally {
        this.creatingRelation = false;
      }
    },

    // ================= EXCLUSÃO =================
    async confirmDeletePatient(patientId) {
      this.patientToDelete = patientId;
      const ok = await this.$confirm({
        type: "delete",
        title: "Confirmar exclusão",
        message: "Tem certeza que deseja remover esta relação médico-paciente?",
        hint: "Esta ação não pode ser desfeita.",
      });
      if (!ok) {
        this.patientToDelete = null;
        return;
      }
      await this.handleDeleteConfirmed();
    },

    async handleDeleteConfirmed() {
      if (!this.patientToDelete || !this.item?.id) return;

      this.deletingRelation = true;

      const doctorId = this.item.id;
      const patientId = this.patientToDelete;

      try {
        await http.delete(
          `/api/relation/doctor-patient/${doctorId}/${patientId}`,
        );

        this.success("Relação excluída com sucesso!");
        await this.getMyPatientsByMyDoctorId();
      } catch (err) {
        if (err.response?.status === 404) {
          this.erroDeRequisicao(err, "Relação não encontrada.");
        } else {
          console.error("Erro ao excluir relação:", err);
          this.erroDeRequisicao(err, "Erro ao excluir relação.");
        }
      } finally {
        this.patientToDelete = null;
        this.deletingRelation = false;
      }
    },
  },
};
</script>

<style scoped>
.add-patient-btn {
  font-size: 16px !important;
  padding: 12px 24px !important;
  border-radius: 8px !important;
}

.patient-card {
  transition: all 0.3s ease;
  border-radius: 8px !important;
}

.patient-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Cabeçalho do stepper: cartão branco sobre o corpo areia */
.steps-header {
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: var(--ds-radius);
  box-shadow: none !important;
}

.clinic-select,
.patient-select {
  margin-bottom: 8px;
}

/* Melhorias nos cards de pacientes */
.v-card-title {
  padding-bottom: 8px !important;
}

/* ═══ Design system dialog ═══ */
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.ds-dialog-header--danger {
  background: linear-gradient(135deg, var(--ds-danger) 0%, var(--ds-danger-mid) 60%, var(--ds-danger-light) 100%) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
